import React, { useState, useRef, useContext, useEffect } from "react";
import { ToastContainer } from "react-toastify";
import { useToastHandler } from "../../../common/ToastHandler";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import AuthContext from "../../.././../utils/secure-route/AuthContext";
import SettingTable from "../ManageSettings/SettingTable";
import "./GeneralLedger.css";
import useSwal from "../../../common/ReusableComponent/useSwal";
import otherincome from "../../../../assets/Image/other income.png";
import CloseIcon from "../../../../assets/Image/close.png";
import useFormattingUtils from "../../../../context/useFormattingUtils";
import CustomDateInput from "../../../../context/CustomDateInput";
import ReusableTable from "../../../common/ReusableComponent/reusableTable/ReusableTable";
import useMyPermissions from "../../../../utils/permissions/useMyPermissions";
import Can from "../../../../utils/permissions/Can";

export default function GeneralLedgerOtherIncome() {
  const { can } = useMyPermissions();
  const { user } = useContext(AuthContext);
  const { Get, Post, Delete, Put, GetPaginatedData } = useApi();
  const storeid = user.store;
  const [banks, setBanks] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [otherIncomeType, setOtherIncomeType] = useState([]);
  const [loading, setLoading] = useState(true);
  const [otherIncome, setOtherIncome] = useState([]);
  const { confirmDelete } = useSwal();
  const { handleToast } = useToastHandler();
  const { formatDate } = useFormattingUtils();
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const amountInputRef = useRef(null);

  const today = new Date().toISOString().split("T")[0];

  const [formData, setFormData] = useState({
    store: storeid,
    sr_no: "",
    date: today,
    transaction_type: "Other Income",
    bank: "",
    amount: "",
    cash_amount: "",
    cheque_amount: "",
    pay_method_status: "pay_now",
    pay_method: "cash",
    total_amount: "",
    note: "",
    owner: null,
    income_type: "",
    income_type_title: "",
    cheque_no: "",
  });

  const columns = [
    { header: "SR", field: "sr_no" },
    { header: "Date", field: "date" },
    { header: "Amount", field: "amount" },
    { header: "Type", field: "other_income" },
    { header: "Payment Type", field: "pay_method" },
  ];

  useEffect(() => {
    fetchOtherIncomeData({ page: 1, searchTerm: "" });
    fetchOtherIncomeType();
    fetchBanks();
  }, []);

  const convertDateToApiFormat = (dateString) => {
    if (!dateString) return null;

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }

    if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      const [month, day, year] = dateString.split('-');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }

    return null;
  };

  const convertToInputFormat = (dateString) => {
    if (!dateString) return today;

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }

    if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      const [month, day, year] = dateString.split('-');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }

    return today;
  };

  const resetFormData = () => {
    setFormData({
      store: storeid,
      sr_no: "",
      date: today,
      transaction_type: "Other Income",
      bank: "",
      amount: "",
      cash_amount: "",
      cheque_amount: "",
      pay_method_status: "pay_now",
      pay_method: "cash",
      total_amount: "",
      note: "",
      owner: null,
      income_type: "",
      income_type_title: "",
      cheque_no: "",
    });
    setIsEditing(false);
    setCurrentRecord(null);
  };

  const toggleModal = () => {
    if (showModal) {
      resetFormData();
    }
    setShowModal(!showModal);

    if (!showModal) {
      setTimeout(() => {
        document.activeElement.blur();
      }, 0);
    }
  };

  const handleAddOtherIncome = async () => {
    try {
      await fetchOtherIncomeType();
      await fetchBanks();
    } catch (error) {
      console.error("Error fetching other income types:", error);
    }

    resetFormData();
    setShowModal(true);
  };

  const fetchOtherIncomeData = async ({ page = 1, searchTerm = "" , sort="" } = {}) => {
    try {
      setLoading(true);

      const response = await GetPaginatedData("transactionData", {
      page,
      search: searchTerm,  
      queryParams: {
        ...(sort && { sort }),
          transaction_type: "Other Income"
      }
    });

      const results = response?.data || [];
      const count = response?.totalCount || 0;
      const totalPages = Math.ceil(count / 10);

      const formatted = results.map((item) => ({
        ...item,
        date: item.date ? formatDate(item.date) : "N/A",
      }));

      setOtherIncome(formatted);
      console.log("Fetched data:", formatted);
      return { data: formatted, totalPages };
    } catch (error) {
      console.error("Error fetching other income data:", error);
      handleToast({
        type: "ERROR",
        message: "Failed to fetch other income data.",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await Get("bank");
      const bankList = response?.results || [];
      setBanks(bankList);
    } catch (error) {
      console.error("Error fetching banks:", error);
      handleToast({
        type: "ERROR",
        message: "Failed to fetch banks.",
      });
    }
  };

  const fetchOtherIncomeType = async () => {
    try {
      const data = await Get("otherIncomeData");
      setOtherIncomeType(data);
    } catch (error) {
      console.error("Error fetching income types:", error);
    }
  };

  const handleDateChange = (e) => {
    const { value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      date: value,
    }));
  };

  const handleAmountChange = (e) => {
    const input = e.target;
    const newValue = input.value;
    const cursorPos = input.selectionStart;
    const oldValue = formData.amount;

    if (newValue.length < oldValue.length) {

      let filteredValue = newValue.replace(/[^0-9.]/g, '');

      const parts = filteredValue.split('.');
      if (parts.length > 2) {
        filteredValue = parts[0] + '.' + parts.slice(1).join('');
      }

      filteredValue = filteredValue.slice(0, 10);

      setFormData((prevData) => ({
        ...prevData,
        amount: filteredValue,
      }));

      setTimeout(() => {
        if (document.activeElement === input) {
          const newPos = Math.min(cursorPos, filteredValue.length);
          input.setSelectionRange(newPos, newPos);
        }
      }, 0);
      return;
    }

    let processedValue = newValue.replace(/[^0-9.]/g, '');

    const decimalParts = processedValue.split('.');
    if (decimalParts.length > 2) {
      processedValue = decimalParts[0] + '.' + decimalParts.slice(1).join('');
    }

    if (decimalParts.length === 2 && decimalParts[1].length > 2) {
      processedValue = decimalParts[0] + '.' + decimalParts[1].substring(0, 2);

      setTimeout(() => {
        if (document.activeElement === input) {
          input.setSelectionRange(processedValue.length, processedValue.length);
        }
      }, 0);
    } else {

      setTimeout(() => {
        if (document.activeElement === input) {
          const lengthDiff = processedValue.length - newValue.length;
          const newPos = Math.min(cursorPos + lengthDiff, processedValue.length);
          input.setSelectionRange(newPos, newPos);
        }
      }, 0);
    }

    processedValue = processedValue.slice(0, 10);

    setFormData((prevData) => ({
      ...prevData,
      amount: processedValue,
    }));
  };

const handleChange = (e) => {
  const { name, value } = e.target;

  if (name === "amount") {
    handleAmountChange(e);
    return;
  }

  if (name === "cheque_no") {
    if (!/^[0-9]*$/.test(value)) {
      return; 
    }
  }

  setFormData((prevData) => ({
    ...prevData,
    [name]: value,
  }));
};

  const handleIncomeChange = (e) => {
    const { name, value } = e.target;

    if (name === "income_type_title") {
      const selectedIncome = otherIncomeType?.find(
        (income) => income.income_type_title === value
      );

      if (!selectedIncome) {
        console.warn(`Income type for "${value}" not found.`);
      }

      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
        income_type: selectedIncome ? selectedIncome.income_type : "",
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleOtherIncome = async (event) => {
    event.preventDefault();

    if (!formData.amount) {
      handleToast({
        type: "ERROR",
        message: "Please enter amount",
      });
      return;
    }

    if (formData.pay_method === "cheque") {
      if (!formData.bank) {
        handleToast({
          type: "ERROR",
          message: "Please select a Bank.",
        });
        return;
      }
      if (!formData.cheque_no) {
        handleToast({
          type: "ERROR",
          message: "Please enter cheque no",
        });
        return;
      }
    }

    if (formData.pay_method === "bank") {
      if (!formData.bank) {
        handleToast({
          type: "ERROR",
          message: "Please select a Bank.",
        });
        return;
      }
    }

    const { sr_no, bank_deposite_type, ...updatedFormData } = formData;

    updatedFormData.other_income = updatedFormData.income_type_title;
    updatedFormData.pay_method_status = null;
    updatedFormData.date = convertDateToApiFormat(formData.date);

    if (!updatedFormData.date) {
      handleToast({
        type: "ERROR",
        message: "Invalid date format. Please check the date."
      });
      return;
    }

    const cleanedFormData = Object.fromEntries(
      Object.entries(updatedFormData).filter(
        ([key, value]) =>
          (value !== "" && value !== null) || key === "pay_method_status"
      )
    );
    try {
      let response;
      if (isEditing && currentRecord?.id) {
        response = await Put(
          "transactionData",
          currentRecord.id,
          updatedFormData
        );

        if (response && (response.status === 200 || response.status === 204)) {
          handleToast({
            type: "SUCCESS",
            message: "Other Income updated successfully!",
          });
          resetFormData();
          setShowModal(false);
          setRefreshTrigger(prev => prev + 1);
        }
      } else {
        response = await Post("transactionData", updatedFormData);

        if (response && (response.status === 200 || response.status === 201)) {
          handleToast({
            type: "SUCCESS",
            message: "Other Income added successfully!",
          });
          resetFormData();
          setShowModal(false);
          setRefreshTrigger(prev => prev + 1);
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      handleToast({
        type: "ERROR",
        message: `Submission failed: ${error.message}`,
      });
    }
  };

  const handleEditOtherIncome = (recordOrId) => {
    console.log("Editing Record:", recordOrId);

    let record;

    if (typeof recordOrId === 'string') {

      record = otherIncome?.find(item => item.id === recordOrId);
      if (!record) {
        console.error("Record not found for ID:", recordOrId);
        handleToast({
          type: "ERROR",
          message: "Error: Record not found.",
        });
        return;
      }
    } else if (recordOrId && typeof recordOrId === 'object' && recordOrId.id) {

      record = recordOrId;
    } else {
      console.error("Invalid record: Missing ID", recordOrId);
      handleToast({
        type: "ERROR",
        message: "Error: Cannot edit record, missing ID.",
      });
      return;
    }

    setFormData({
      store: storeid,
      sr_no: record.sr_no || "",
      date: convertToInputFormat(record.date),
      transaction_type: "Other Income",
      bank: record.bank || "",
      amount: record.amount || "",
      cash_amount: record.cash_amount || "",
      cheque_amount: record.cheque_amount || "",
      pay_method_status: record.pay_method_status || "pay_now",
      pay_method: record.pay_method || "cash",
      total_amount: record.total_amount || "",
      note: record.note || "",
      owner: record.owner || null,
      income_type: record.income_type || "",
      income_type_title: record.other_income || "",
      cheque_no: record.cheque_no || "",
    });

    setCurrentRecord(record);
    setIsEditing(true);
    setShowModal(true);
  };

  const handleDeleteOtherIncome = async (id) => {
    const result = await confirmDelete();
    if (!result.isConfirmed) return;

    try {
      await Delete("transactionData", id);

      setOtherIncome((prevOtherIncome) =>
        prevOtherIncome.filter((otherincome) => otherincome.id !== id)
      );

      handleToast({
        type: "SUCCESS",
        message: "Other income deleted successfully"
      });

      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Error deleting other Income:", error);
      handleToast({
        type: "ERROR",
        message: "Error deleting other Income: " + error.message
      });
    }
  };

  return (
    <>
      <ToastContainer />
      <div className="">
        <div className="responsive-box">
          <div className="header-row mb-4">
            <div className="setting-title">Other Income</div>
            <Can module="utilService" action="add">
            <button
              className="btn cus d-flex align-items-center"
              onClick={handleAddOtherIncome}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: "#FFAF32",
                boxShadow: "none",
                outline: "none",
                width: "auto",
                height: "42px",
                padding: "8px 12px",
                fontSize: "14px",
                border: "none",
                borderRadius: "10px",
                color: "#000",
                cursor: "pointer",
                fontWeight: "bold",
                gap: "8px",
              }}
            >
              Add Other Income
              <img
                src={otherincome}
                alt="Bank Deposite Icon"
                style={{ width: "30px", height: "30px", marginLeft: "18px" }}
              />
            </button>
            </Can>
          </div>

          {showModal && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0,0,0,0.5)",
                zIndex: 1000,
              }}
              onClick={toggleModal}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: "700px",
                  backgroundColor: "white",
                  margin: "100px auto",
                  borderRadius: "20px",
                  position: "relative",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                }}
              >
                <div className="modal-header" style={{ border: "none" }}>
                  <h5 className="modal-title p-2" style={{ fontWeight: "bold" }}>
                    {isEditing ? "Edit Other Income" : "Add Other Income"}
                  </h5>
                  <img
                    src={CloseIcon}
                    alt="Close"
                    width="34"
                    height="34"
                    onClick={toggleModal}
                    style={{ cursor: "pointer" }}
                  />
                </div>
                <div className="modal-body">
                  <form onSubmit={handleOtherIncome}>
                    <div className="form-row form-group col-md-12 d-flex align-items-center">
                      <div style={{ flex: "1" }}>
                        <CustomDateInput
                          value={formData.date || today}
                          onChange={handleDateChange}
                          required
                          readOnly
                          inputStyle={{
                            marginLeft: "37px",
                            height: "52px",
                            width: "269px",
                            marginTop: '20px',
                          }}
                        />
                      </div>

                      <div style={{ flex: "1" }}>
                        <select
                          style={{
                            marginLeft: "25px",
                            height: "52px",
                            width: "269px",
                          }}
                          className="input-select"
                          name="income_type_title"
                          value={formData.income_type_title || ""}
                          onChange={handleIncomeChange}
                          required
                        >
                          <option value="">Select Income Type</option>
                          {otherIncomeType?.length > 0 ? (
                            otherIncomeType.map((otherincome) => (
                              <option
                                key={otherincome.id}
                                value={otherincome.income_type_title}
                              >
                                {otherincome.title || otherincome.income_type_title}
                              </option>
                            ))
                          ) : (
                            <option value="" disabled>Loading income types...</option>
                          )}
                        </select>
                      </div>
                    </div>

                    <div className="form-row d-flex col-md-6">
                      <div className="containercolor">
                        <div
                          className="input-group p-2 mt-2"
                          style={{
                            height: "62px",
                            marginLeft: "30px",
                            width: "283px",
                            marginBottom: '10px'
                          }}
                        >
                          <input
                            ref={amountInputRef}
                            type="text"
                            name="amount"
                            className="form-control-borderless"
                            value={formData.amount || ""}
                            onChange={handleChange}
                            placeholder="Amount"
                            aria-label="Username"
                            aria-describedby="addon-wrapping"
                          />
                        </div>
                      </div>
                    </div>

                    <div
                      className="d-flex justify-content-center align-items-center mt-4"
                      style={{
                        border: "1px solid #ACB4AA",
                        padding: "5px 5px",
                        width: "fit-content",
                        borderRadius: "40px",
                        marginTop: "10px",
                        marginBottom: "20px",
                        marginLeft: "35px",
                        marginRight: "auto",
                        gap: "10px",
                      }}
                    >
                      <div
                        className="form-check"
                        style={{
                          backgroundColor:
                            formData.pay_method === "cash"
                              ? "#4a5546"
                              : "transparent",
                          borderRadius: "20px",
                          padding: "6px 15px",
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          handleChange({
                            target: { name: "pay_method", value: "cash" },
                          })
                        }
                      >
                        <input
                          type="radio"
                          className="form-check-input"
                          name="pay_method"
                          value="cash"
                          checked={formData.pay_method === "cash"}
                          onChange={handleChange}
                          style={{ display: "none" }}
                        />
                        <label
                          className="form-check-label"
                          style={{
                            color:
                              formData.pay_method === "cash" ? "#fff" : "#000",
                          }}
                        >
                          Cash
                        </label>
                      </div>

                      <div
                        className="form-check"
                        style={{
                          backgroundColor:
                            formData.pay_method === "bank"
                              ? "#4a5546"
                              : "transparent",
                          borderRadius: "20px",
                          padding: "6px 15px",
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          handleChange({
                            target: { name: "pay_method", value: "bank" },
                          })
                        }
                      >
                        <input
                          required
                          type="radio"
                          className="form-check-input"
                          name="pay_method"
                          value="bank"
                          checked={formData.pay_method === "bank"}
                          onChange={handleChange}
                          style={{ display: "none" }}
                        />
                        <label
                          className="form-check-label"
                          style={{
                            color:
                              formData.pay_method === "bank" ? "#fff" : "#000",
                          }}
                        >
                          Bank Card (ACH/EFT)
                        </label>
                      </div>

                      <div
                        className="form-check"
                        style={{
                          backgroundColor:
                            formData.pay_method === "cheque"
                              ? "#4a5546"
                              : "transparent",
                          borderRadius: "20px",
                          padding: "6px 15px",
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          handleChange({
                            target: { name: "pay_method", value: "cheque" },
                          })
                        }
                      >
                        <input
                          type="radio"
                          className="form-check-input"
                          name="pay_method"
                          value="cheque"
                          checked={formData.pay_method === "cheque"}
                          onChange={handleChange}
                          style={{ display: "none" }}
                        />
                        <label
                          className="form-check-label"
                          style={{
                            color:
                              formData.pay_method === "cheque" ? "#fff" : "#000",
                          }}
                        >
                          Cheque
                        </label>
                      </div>
                    </div>

                    <div className="row">
                      {(formData.pay_method === "bank" ||
                        formData.pay_method === "cheque") && (
                          <div className="mb-3 mt-2 col-md-4">
                            <select
                              style={{
                                marginLeft: "40px",
                                height: "52px",
                              }}
                              className="input-select"
                              name="bank"
                              value={formData.bank || ""}
                              onChange={handleChange}
                            >
                              <option value="">Select a bank</option>
                              {banks?.length > 0 ? (
                                banks.map((bank) => (
                                  <option key={bank.id} value={bank.id}>
                                    {bank.name}
                                  </option>
                                ))
                              ) : (
                                <option value="" disabled>Loading banks...</option>
                              )}
                            </select>
                          </div>
                        )}

                      {formData.pay_method === "cheque" && (
                        <div className="mb-3 mt-2 col-md-4">
                          <input
                            style={{
                              marginLeft: "50px",
                              border: "none",
                              height: "52px",
                              borderBottom: "1px solid #ccc",
                            }}
                            type="text"
                            name="cheque_no"
                            value={formData.cheque_no || ""}
                            placeholder="Cheque No"
                            onChange={handleChange}
                            pattern="[0-9]*"
                            inputMode="numeric"
                          />
                        </div>
                      )}
                    </div>

                    <div className="modal-footer" style={{ border: "none" }}>
                      <button
                        type="button"
                        className="btn btn-secondary-outline"
                        onClick={toggleModal}
                        style={{
                          border: " 1px solid #282e26",
                          borderRadius: "50px",
                          width: "181px",
                          height: "45px",
                          fontWeight: "bold",
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn"
                        style={{
                          color: "white",
                          backgroundColor: "#282e26",
                          borderRadius: "50px",
                          width: "181px",
                          height: "45px",
                          fontWeight: "bold",
                        }}
                      >
                        Submit
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          <ReusableTable
            fetchData={fetchOtherIncomeData}
            columns={columns}
            enablePagination={true}
            itemsPerPage={10}
            key={refreshTrigger}
            showFooter={true}
            showAction={true}
            showExport={true}
            showFilter={false}
            onEdit={can("utilService", "change") ? handleEditOtherIncome : undefined}
            onDelete={can("utilService", "delete") ? handleDeleteOtherIncome : undefined}
          />
        </div>
      </div>
    </>
  );
}